import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Services
import WorkspaceService from "services/workspace-service";

// Components
import Plan from "layouts/profile/components/Subscription/plan";

function UpgradeBanner() {
    const [activeSubscription, setActiveSubscription] = useState(null);
    const [loading, setLoading] = useState(true);
    const [openPlanDialog, setOpenPlanDialog] = useState(false);

    // Fetch Subscription Data
    const fetchWorkspaceSubscription = async () => {
        try {
            const response = await WorkspaceService.fetchWorkspaceSubscription();

            // Extract Active Subscription
            const active = response.find(sub => sub.status.toLowerCase() === "active");

            if (active) {
                setActiveSubscription({
                    plan: active.plan || "N/A",
                    renewalDate: active.next_renewal_date
                        ? new Date(active.next_renewal_date).toLocaleDateString()
                        : null,
                });
            }
        } catch (error) {
            console.error("Error fetching subscription for banner:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchWorkspaceSubscription();
    }, []);

    // Check if Trial User is ending soon (<= 1 day)
    const isTrialEndingSoon = () => {
        if (!activeSubscription) return false;

        const { plan, renewalDate } = activeSubscription;
        if (plan.toLowerCase() !== "trial" || !renewalDate) return false;

        // Parse DD/MM/YYYY vs MM/DD/YYYY depending on locale. 
        // Assuming backend returns valid ISO or strictly formatted date.
        // The previous code used split("/"), implying format DD/MM/YYYY from toLocaleDateString() which is risky.
        // Ideally use valid Date object. But keeping consistency with previous logic:

        // Safer to construct Date from the stored 'renewalDate' string if it came from toLocaleDateString()
        // NOTE: toLocaleDateString() format varies by server locale. 
        // We should rely on the raw ISO string if possible, but here we only have what we saved.
        // Let's assume standard behavior or that the previous logic worked for the user's locale.

        const [day, month, year] = renewalDate.split("/").map(Number);
        const dateObj = new Date(year, month - 1, day);

        const now = new Date();
        const diffInMs = dateObj - now;
        const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

        return diffInDays <= 1;
    };

    if (loading || !isTrialEndingSoon()) {
        return null;
    }

    return (
        <>
            <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                py={0.5}
                px={2}
                sx={{
                    background: "linear-gradient(135deg, #FF5722 0%, #FF5722 100%)",
                    borderRadius: "12px",
                    boxShadow: "0 4px 20px 0 rgba(255, 152, 0, 0.3)",
                    color: "white",
                    width: "100%",
                    maxWidth: "800px",
                    margin: "0 auto",
                    position: "relative",
                    overflow: "hidden",
                }}
            >
                {/* Glass effect overlay */}
                <MDBox
                    position="absolute"
                    top={0}
                    left={0}
                    width="100%"
                    height="100%"
                    sx={{
                        background: "rgba(255, 255, 255, 0.1)",
                        backdropFilter: "blur(10px)",
                        zIndex: 0,
                    }}
                />

                <MDBox position="relative" zIndex={1} display="flex" alignItems="center" justifyContent="space-between" width="100%" gap={2}>
                    <MDBox display="flex" alignItems="center" gap={2}>
                        <MDBox
                            variant="gradient"
                            bgColor="warning"
                            coloredShadow="warning"
                            borderRadius="xl"
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            width="2.5rem"
                            height="2.5rem"
                            sx={{ boxShadow: "0 2px 6px rgba(0,0,0,0.12)" }}
                        >
                            <Icon fontSize="medium" sx={{ color: "#000 !important" }}>workspace_premium</Icon>
                        </MDBox>
                        <MDBox>
                            <MDTypography variant="subtitle2" color="white" fontWeight="bold" lineHeight={0.2} mt={1.5}>
                                Your Free Trial is Ending Soon!
                            </MDTypography>
                            <MDTypography variant="caption" color="white" opacity={0.9} lineHeight={1}>
                                Upgrade now to keep accessing powerful insights and premium features.
                            </MDTypography>
                        </MDBox>
                    </MDBox>

                    <MDButton
                        variant="contained"
                        color="accent"
                        size="small"
                        onClick={() => setOpenPlanDialog(true)}

                    >
                        Upgrade Now
                    </MDButton>
                </MDBox>
            </MDBox>

            {/* Upgrade Plan Dialog */}
            <Dialog
                open={openPlanDialog}
                onClose={() => setOpenPlanDialog(false)}
                maxWidth="lg"
                fullWidth
            >
                <Plan />
            </Dialog>
        </>
    );
}

export default UpgradeBanner;
